import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ChevronDown, ChevronRight, GripVertical, Info, Loader2, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  adminEisf,
  type ApprovalMode,
  type DocumentTypeApproverInput,
  type DocumentTypeRow,
  type DocumentTypeEditable,
} from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { humanRole } from '@/lib/humanise';
import { TRAINING_ROLE_OPTIONS } from '@/lib/trainingRoles';
import { REQUIRED_FIELD_OPTIONS } from '@/lib/requiredFieldOptions';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Sentinel value used in the Zone / Section dropdowns to mean
 * "create a new one inline." Anything the taxonomy could legitimately
 * emit (DIA zone names, DIA section codes like "1.1", custom section
 * codes like "custom.5") cannot collide with these strings.
 */
const OTHER_ZONE_SENTINEL = '__other_zone__';
const OTHER_SECTION_SENTINEL = '__other_section__';

/**
 * Sprint-15 + Feedback #16 (2026-07-28) — Edit / Create PAGE for a
 * master-data document type. Originally a modal; rebuilt as a full page
 * because the form is too tall for a dialog (six sections, ~22 fields,
 * mandatory Advanced accordion) and users kept losing their edits when
 * they clicked outside to reach adjacent context.
 *
 * Routes:
 *   /admin/eisf-master-data/document-types/new                    (create)
 *   /admin/eisf-master-data/document-types/new?prefill=<name>     (create, prefilled)
 *   /admin/eisf-master-data/document-types/:code/edit             (edit)
 *
 * In edit mode the slug (`document_type_code`) is read-only — every binder
 * slot references it as a natural key, so renaming would orphan rows.
 * In create mode the user types the slug; the schema enforces kebab-case.
 */

type Mode = 'edit' | 'create';

/** Role enum values — mirrors `Role` in DreamIT-Backend/src/types/domain.ts. */
const ROLES = [
  'platform_admin',
  'application_admin',
  'compliance',
  'study_designer',
  'site_admin',
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'site_study_designer',
  'regulatory_specialist',
  'site_pharma',
  'finance_billing',
  'monitor',
  'auditor',
  'irb_administrator',
  'sponsor_regulatory_affairs',
  'regulatory_inspector',
  'data_manager',
  'sponsor_project_manager',
] as const;

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

/**
 * Sprint-15 — Canonical vocabularies for the picker fields.
 *
 * Storage shape stays as it was on the BE (JSON-array-as-string for the
 * `*_json` columns, plain string for the enum-ish columns). The form
 * presents structured pickers; we serialize / parse at the I/O boundary
 * so admins never edit raw JSON.
 *
 * Vocabularies are sourced from the existing 88-row catalog seed (see
 * `DreamIT-Backend/src/data/documentTypes.ts`) plus the BE allowlist
 * for workflows. Legacy values that don't match the canonical list are
 * preserved with a "(legacy)" suffix in each picker so editing a row
 * doesn't silently drop them.
 */

const ROLES_FOR_CREATE = ['Site Admins', 'Site Staff'] as const;

const DRAFT_WORKFLOW_OPTIONS = [
  { value: 'Send for PI Signature', label: 'Send for PI signature' },
  { value: 'Send for Review', label: 'Send for review' },
  { value: 'Send for eSignature Approval', label: 'Send for eSignature approval' },
  { value: 'Send for Read and Understand', label: 'Send for read and understand' },
  { value: 'Change Status to {steady state}', label: 'Move to steady state' },
] as const;

const STEADY_WORKFLOW_OPTIONS = [
  { value: 'Send for Read and Understand', label: 'Send for read and understand' },
  { value: 'Change State to Superseded (Admins)', label: 'Mark as superseded' },
  { value: 'Delete All Versions (Admins)', label: 'Delete all versions' },
  { value: 'Perform Copy Certification', label: 'Certify a copy' },
] as const;

const MONITOR_VIEW_MODES = ['Yes', 'No', 'Conditional'] as const;
type MonitorViewMode = (typeof MONITOR_VIEW_MODES)[number];

const DRAFT_VERSIONING_MODES = ['Yes', 'No', 'Custom'] as const;
type DraftVersioningMode = (typeof DRAFT_VERSIONING_MODES)[number];

/**
 * Parse a JSON-array-of-strings column from the catalog. Returns [] on
 * malformed or empty input so the picker still renders. Non-string items
 * are stringified to survive partially-broken seed data.
 */
function parseJsonStringArray(s: string | null | undefined): string[] {
  if (!s) return [];
  try {
    const parsed = JSON.parse(s);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((v) => (typeof v === 'string' ? v : String(v)));
  } catch {
    return [];
  }
}

/** Serialize back to the storage shape. */
function composeJsonStringArray(arr: string[]): string {
  return JSON.stringify(arr);
}

/**
 * Lifecycle is stored in the catalog as a trajectory string ("Draft to X").
 * Sprint-15 simplification per client spec: the platform admin only needs
 * to decide ONE thing — does this type get locked after approval?
 *
 *   - Locked         -> stored as "Draft to Final"
 *   - Replaceable    -> stored as "Draft to Current"
 *
 * The audit-log runtime status taxonomy (Draft / In Review / Approved /
 * Effective / Superseded / Archived per client spec) is the VERSION-LEVEL
 * state machine — it's not part of this per-type config.
 *
 * Legacy end-states from earlier seed data (Approved for Use, Fully Executed)
 * are surfaced as "(legacy)" so editing a row doesn't silently change behavior
 * to a different lock semantic. All three legacy values (Final, Approved for
 * Use, Fully Executed) are semantically "locked", so they default the toggle
 * to Locked. Only "Current" maps to Replaceable.
 */
const LIFECYCLE_PREFIX = 'Draft to ';
const LIFECYCLE_LOCKED = 'Draft to Final';
const LIFECYCLE_REPLACEABLE = 'Draft to Current';
const LEGACY_LOCKED_END_STATES = ['Approved for Use', 'Fully Executed'] as const;

interface LifecycleLockSemantics {
  locked: boolean;
  /** The non-canonical end-state token, surfaced as "(legacy: …)" in the UI. Null when the stored value is one of the two canonical strings. */
  legacyEndState: string | null;
}

/** Parse "Draft to X" into { locked, legacyEndState }. */
function parseLifecycleLock(stored: string): LifecycleLockSemantics {
  if (stored === LIFECYCLE_LOCKED) return { locked: true, legacyEndState: null };
  if (stored === LIFECYCLE_REPLACEABLE) return { locked: false, legacyEndState: null };
  if (stored.startsWith(LIFECYCLE_PREFIX)) {
    const end = stored.slice(LIFECYCLE_PREFIX.length);
    // Legacy "Approved for Use" / "Fully Executed" both mean locked.
    if ((LEGACY_LOCKED_END_STATES as readonly string[]).includes(end)) {
      return { locked: true, legacyEndState: end };
    }
    // Unknown end state — surface as legacy and default to the safer
    // (Locked) interpretation. Admin sees the original value and chooses.
    return { locked: true, legacyEndState: end };
  }
  // Empty or non-"Draft to" — treat as locked but flag the value.
  return { locked: true, legacyEndState: stored || null };
}

/** Compose the storage string from the toggle. Loses any legacy end-state token. */
function composeLifecycleFromLock(locked: boolean): string {
  return locked ? LIFECYCLE_LOCKED : LIFECYCLE_REPLACEABLE;
}

type FormShape = DocumentTypeEditable & {
  document_type_code: string;
  approvers: DocumentTypeApproverInput[];
};

const BLANK: FormShape = {
  document_type_code: '',
  document_type_name: '',
  definition: '',
  lifecycle: LIFECYCLE_LOCKED,
  who_can_create_json: '[]',
  can_create_site_admin: false,
  can_create_site_staff: false,
  required_fields_json: '[]',
  create_new_draft_versions: 'No',
  draft_state_workflows_json: '[]',
  steady_state_workflows_json: '[]',
  can_monitors_view: 'No',
  monitor_view_conditions: null,
  site_viewer_can_download: false,
  monitor_can_download: false,
  shareable_via_myveeva: false,
  approval_mode: 'single',
  any_of_threshold: null,
  training_required: false,
  training_required_roles: null,
  source_notes: null,
  is_active: true,
  visible_to_site_users: false,
  is_mandatory: false,
  parent_section_code: null,
  approvers: [],
};

/**
 * Default §11.50 signature meanings per role — surfaced when the admin
 * adds a new approver row. Admin can overwrite freely. Empty fallback
 * for sponsor/regulator personas where the meaning is more context-
 * dependent.
 */
const DEFAULT_SIGNATURE_MEANINGS: Record<string, string> = {
  principal_investigator: 'I approve this document for use at my site.',
  sub_investigator: 'I have reviewed and concur with this document.',
  coordinator: 'I prepared and verified this document.',
  regulatory_specialist: 'I have reviewed this document for regulatory compliance.',
  site_admin: 'I authorize this document for site use.',
  site_pharma: 'I have reviewed the IP-related content of this document.',
  monitor: 'I have monitored this document.',
  auditor: 'I have audited this document.',
  irb_administrator: 'I confirm IRB approval of this document.',
};

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

type SectionId = 'filing' | 'identity' | 'lifecycle' | 'permissions' | 'workflows' | 'notes';
type SectionStatusMap = Record<SectionId, { hasError: boolean }>;

const TOC_ENTRIES: { id: SectionId; label: string }[] = [
  { id: 'filing', label: 'Filing location' },
  { id: 'identity', label: 'Identity' },
  { id: 'lifecycle', label: 'Lifecycle & approval' },
  { id: 'permissions', label: 'Permissions & visibility' },
  { id: 'workflows', label: 'Workflows & naming' },
  { id: 'notes', label: 'Notes (optional)' },
];

function TocSidebar({
  states,
  onJump,
}: {
  states: SectionStatusMap;
  onJump: (id: SectionId) => void;
}) {
  return (
    <aside className="hidden lg:block">
      <nav
        aria-label="Form sections"
        className="sticky top-4 space-y-0.5 rounded-lg border border-surface-border bg-white p-2 shadow-sm"
      >
        {TOC_ENTRIES.map((e) => (
          <button
            key={e.id}
            type="button"
            onClick={() => onJump(e.id)}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] text-ink-primary hover:bg-surface-muted/60"
          >
            <span
              className={
                'h-1.5 w-1.5 shrink-0 rounded-full '
                + (states[e.id].hasError ? 'bg-danger-500' : 'bg-surface-border')
              }
            />
            <span className="truncate">{e.label}</span>
          </button>
        ))}
      </nav>
    </aside>
  );
}

export function DocumentTypeFormPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { code: codeParam } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const mode: Mode = codeParam ? 'edit' : 'create';
  const code = codeParam;
  const prefillName = searchParams.get('prefill') ?? undefined;
  // Optional deep-link from the eISF Catalog tree: sub-artifact leaf click
  // now carries the section code so the picker prefills to the section the
  // user clicked from. Falls back to unset if the caller was the top-level
  // "+ Add document type" button (no section context).
  const prefillSectionCode = searchParams.get('section') ?? undefined;

  const goBackToList = () => {
    navigate('/admin/eisf-master-data');
  };
  const onSaved = (row: DocumentTypeRow) => {
    // Invalidate the master-data caches so the list picks up the new row.
    qc.invalidateQueries({ queryKey: ['adminEisf', 'document-types'] });
    qc.invalidateQueries({ queryKey: ['adminEisf', 'document-type', row.document_type_code] });
    goBackToList();
  };

  // In edit mode, fetch the row first.
  const existingQ = useQuery({
    queryKey: ['adminEisf', 'document-type', code],
    queryFn: () => adminEisf.getDocumentType(code!),
    enabled: mode === 'edit' && !!code,
  });

  const [form, setForm] = useState<FormShape>(() => {
    if (mode === 'create' && prefillName && prefillName.trim()) {
      return {
        ...BLANK,
        document_type_name: prefillName.trim(),
        document_type_code: slugify(prefillName),
        parent_section_code: prefillSectionCode ?? null,
      };
    }
    if (mode === 'create' && prefillSectionCode) {
      return { ...BLANK, parent_section_code: prefillSectionCode };
    }
    return BLANK;
  });

  // Taxonomy for the Zone > Section picker. Cached across the whole
  // admin session - the shape only changes when the eISF Reference
  // xlsx is regenerated and redeployed.
  const taxonomyQ = useQuery({
    queryKey: ['adminEisf', 'taxonomy'],
    queryFn: () => adminEisf.getTaxonomy(),
    staleTime: 60 * 60_000,
  });
  const zones = taxonomyQ.data?.zones ?? [];

  // Derive the effective zone from the currently-selected section. Local
  // zoneOverride lets the user change the Zone dropdown to an empty
  // Section state (they pick a Zone first, then Section fills in).
  const derivedZone = useMemo(() => {
    for (const z of zones) {
      if (z.sections.some((sec) => sec.code === form.parent_section_code)) {
        return z.zone;
      }
    }
    return '';
  }, [zones, form.parent_section_code]);
  const [zoneOverride, setZoneOverride] = useState<string>('');
  const selectedZoneName = zoneOverride || derivedZone;
  const sectionsInSelectedZone = useMemo(
    () => zones.find((z) => z.zone === selectedZoneName)?.sections ?? [],
    [zones, selectedZoneName],
  );
  const selectedSection = useMemo(
    () => sectionsInSelectedZone.find((s) => s.code === form.parent_section_code) ?? null,
    [sectionsInSelectedZone, form.parent_section_code],
  );

  // Inline "Other" state - only platform admins can create custom
  // zones/sections; site admins see picker options only.
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === 'platform_admin' || user?.role === 'application_admin';
  const [zoneMode, setZoneMode] = useState<'pick' | 'other'>('pick');
  const [sectionMode, setSectionMode] = useState<'pick' | 'other'>('pick');
  const [newZoneName, setNewZoneName] = useState('');
  const [newSection, setNewSection] = useState<{
    name: string;
    inclusion: 'core' | 'recommended';
  }>({ name: '', inclusion: 'recommended' });
  const [touched, setTouched] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Seed the form once the existing row arrives. Strip non-editable
  // fields (audit timestamps, derived bools, source_version) — the BE
  // ignores them on PATCH but sending them adds noise to the diff audit.
  useEffect(() => {
    if (mode === 'edit' && existingQ.data) {
      const r = existingQ.data.document_type;
      setForm({
        document_type_code: r.document_type_code,
        document_type_name: r.document_type_name,
        definition: r.definition,
        lifecycle: r.lifecycle,
        who_can_create_json: r.who_can_create_json,
        can_create_site_admin: r.can_create_site_admin,
        can_create_site_staff: r.can_create_site_staff,
        required_fields_json: r.required_fields_json,
        create_new_draft_versions: r.create_new_draft_versions,
        draft_state_workflows_json: r.draft_state_workflows_json,
        steady_state_workflows_json: r.steady_state_workflows_json,
        can_monitors_view: r.can_monitors_view,
        monitor_view_conditions: r.monitor_view_conditions,
        site_viewer_can_download: r.site_viewer_can_download,
        monitor_can_download: r.monitor_can_download,
        shareable_via_myveeva: r.shareable_via_myveeva,
        approval_mode: r.approval_mode,
        any_of_threshold: r.any_of_threshold,
        training_required: r.training_required ?? false,
        training_required_roles: r.training_required_roles
          ? parseJsonStringArray(r.training_required_roles)
          : null,
        source_notes: r.source_notes,
        is_active: r.is_active,
        visible_to_site_users: r.visible_to_site_users === 'true',
        is_mandatory: r.is_mandatory === 'true',
        parent_section_code: r.parent_section_code ?? null,
        approvers: existingQ.data.approvers.map((a) => ({
          role: a.role,
          sequence_order: a.sequence_order,
          signature_meaning: a.signature_meaning,
        })),
      });
      // Always reveal Advanced in edit mode. It holds the required
      // naming_format_template — hiding it while the row is invalid
      // would leave the admin staring at a "Save" button that never
      // enables, with no clue why.
      setAdvancedOpen(true);
    }
  }, [mode, existingQ.data]);

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
    setTouched(true);
  }

  // Filing-location validation lives here (not in validate()) because it
  // needs the pickers' local state (zoneMode / sectionMode / new_zone /
  // new_section), which are not part of `form`.
  const filingLocationError = useMemo<string | undefined>(() => {
    if (mode !== 'create') return undefined;
    // Path 1: pick existing section
    if (sectionMode === 'pick' && form.parent_section_code) return undefined;
    // Path 2: new_section (with either new_zone OR existing zone)
    if (sectionMode === 'other') {
      if (!newSection.name.trim()) return 'New section name is required.';
      if (zoneMode === 'other') {
        if (!newZoneName.trim()) return 'New zone name is required.';
        return undefined;
      }
      if (!selectedZoneName) return 'Pick a parent zone (or choose "Other…" to create a new one).';
      return undefined;
    }
    return 'Pick a Zone and Section (or "Other…" to create a new one).';
  }, [mode, sectionMode, zoneMode, form.parent_section_code, newSection, newZoneName, selectedZoneName]);

  const validationErrors = useMemo(() => {
    const errs = validate(form, mode);
    if (filingLocationError) errs.parent_section_code = filingLocationError;
    return errs;
  }, [form, mode, filingLocationError]);
  const isValid = Object.keys(validationErrors).length === 0;

  useEffect(() => {
    if (mode !== 'create') return;
    const nextCode = slugify(form.document_type_name);
    if (form.document_type_code !== nextCode) {
      setForm((f) => ({ ...f, document_type_code: nextCode }));
    }
  }, [mode, form.document_type_name, form.document_type_code]);

  useEffect(() => {
    const roles = parseJsonStringArray(form.who_can_create_json).map((r) => r.toLowerCase());
    const nextAdmin = roles.includes('site admins');
    const nextStaff = roles.includes('site staff');
    if (form.can_create_site_admin !== nextAdmin || form.can_create_site_staff !== nextStaff) {
      setForm((f) => ({
        ...f,
        can_create_site_admin: nextAdmin,
        can_create_site_staff: nextStaff,
      }));
    }
  }, [form.who_can_create_json, form.can_create_site_admin, form.can_create_site_staff]);

  const createM = useMutation({
    mutationFn: () => {
      const { parent_section_code: rawFk, document_type_code: _skipCode, ...rest } = form;
      void _skipCode;
      const payload: Parameters<typeof adminEisf.createDocumentType>[0] = {
        ...rest,
      };
      if (sectionMode === 'other') {
        payload.new_section = {
          name: newSection.name.trim(),
          inclusion: newSection.inclusion,
          ...(zoneMode === 'pick' && selectedZoneName
            ? { parent_zone_name: selectedZoneName }
            : {}),
        };
        if (zoneMode === 'other') {
          payload.new_zone = { name: newZoneName.trim() };
        }
      } else {
        payload.parent_section_code = rawFk ?? undefined;
      }
      return adminEisf.createDocumentType(payload);
    },
    onSuccess: (r) => {
      toast.success(`"${r.document_type.document_type_name}" added to the catalog`);
      onSaved(r.document_type);
    },
    onError: (err: unknown) => {
      const msg = errorText(err, 'Create failed');
      toast.error(msg);
    },
  });

  const updateM = useMutation({
    mutationFn: () => {
      const { document_type_code, ...patch } = form;
      void document_type_code;
      return adminEisf.updateDocumentType(form.document_type_code, patch);
    },
    onSuccess: (r) => {
      toast.success(`"${r.document_type.document_type_name}" updated`);
      onSaved(r.document_type);
    },
    onError: (err: unknown) => {
      const msg = errorText(err, 'Update failed');
      toast.error(msg);
    },
  });

  const notesAccordionHasError = 'required_fields_json' in validationErrors;

  function submit() {
    if (!isValid) {
      setTouched(true);
      if (notesAccordionHasError) setAdvancedOpen(true);
      return;
    }
    if (mode === 'create') createM.mutate();
    else updateM.mutate();
  }

  const isSaving = createM.isPending || updateM.isPending;

  const sectionStates = useMemo<SectionStatusMap>(() => {
    const errKey = (k: string) => (touched ? !!validationErrors[k] : false);
    return {
      filing: { hasError: errKey('parent_section_code') },
      identity: { hasError: errKey('document_type_name') || errKey('document_type_code') },
      lifecycle: { hasError: errKey('approvers') || errKey('any_of_threshold') },
      permissions: { hasError: errKey('who_can_create_json') },
      workflows: {
        hasError:
          errKey('draft_state_workflows_json')
          || errKey('steady_state_workflows_json'),
      },
      notes: { hasError: errKey('required_fields_json') },
    };
  }, [touched, validationErrors]);

  const pageTitle = mode === 'create'
    ? 'Add document type'
    : `Edit — ${form.document_type_name || existingQ.data?.document_type.document_type_name || ''}`;

  return (
    <PageHeader
      variant="fullscreen"
      backTo="/admin/eisf-master-data"
      title={pageTitle}
      subtitle="Master data · changes propagate to every tenant immediately."
      actions={
        <>
          <button
            type="button"
            onClick={goBackToList}
            disabled={isSaving}
            className="rounded border border-surface-border px-3 py-1.5 text-sm font-medium text-ink-primary hover:bg-surface-muted/50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={isSaving || (touched && !isValid)}
            className="inline-flex items-center gap-1.5 rounded bg-accent-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-60"
          >
            {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {mode === 'create'
              ? (isSaving ? 'Creating…' : 'Create')
              : (isSaving ? 'Saving…' : 'Save')}
          </button>
        </>
      }
    >
      <>
        {mode === 'edit' && existingQ.isLoading && (
          <div className="flex w-full items-center gap-2 px-6 py-10 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {mode === 'edit' && existingQ.isError && (
          <div className="w-full px-6 py-6">
            <div className={`flex items-start gap-2 rounded border p-3 text-xs ${errorToneClass(existingQ.error)}`}>
              {isPermissionError(existingQ.error) ? <Info className="mt-0.5 h-3.5 w-3.5 shrink-0"/> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
              <span>
                {errorText(existingQ.error, 'Failed to load this type.')}
              </span>
            </div>
          </div>
        )}

        {(mode === 'create' || existingQ.data) && (
          <div className="grid w-full gap-6 px-6 py-6 lg:grid-cols-[200px,1fr]">
            <TocSidebar
              states={sectionStates}
              onJump={(id) => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
            <main className="space-y-6">
              <Section
                id="filing"
                title="Filing location"
                intro={
                  isPlatformAdmin && mode === 'create'
                    ? 'Where this type lives in the DIA taxonomy. Pick "Other…" to add a new zone or section inline.'
                    : 'Where this type lives in the DIA taxonomy. Drives auto-file and the Catalog tree.'
                }
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Zone" required error={touched ? validationErrors.parent_section_code : undefined}>
                    <select
                      value={zoneMode === 'other' ? OTHER_ZONE_SENTINEL : selectedZoneName}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === OTHER_ZONE_SENTINEL) {
                          setZoneMode('other');
                          setZoneOverride('');
                          // Clear section too - a new zone means no existing section
                          // could be its child. Force the operator to pick.
                          set('parent_section_code', null);
                          setSectionMode('other');
                          return;
                        }
                        setZoneMode('pick');
                        setNewZoneName('');
                        setZoneOverride(v);
                        if (form.parent_section_code) {
                          const stillValid = (zones.find((z) => z.zone === v)?.sections ?? [])
                            .some((s) => s.code === form.parent_section_code);
                          if (!stillValid) set('parent_section_code', null);
                        }
                        setSectionMode('pick');
                      }}
                      disabled={taxonomyQ.isLoading}
                      className="input w-full"
                    >
                      <option value="">— Select zone —</option>
                      {zones.map((z) => (
                        <option key={z.zone} value={z.zone}>{z.zone}</option>
                      ))}
                      {isPlatformAdmin && mode === 'create' && (
                        <option value={OTHER_ZONE_SENTINEL}>+ Other… (add a new zone)</option>
                      )}
                    </select>
                    {zoneMode === 'other' && (
                      <input
                        type="text"
                        value={newZoneName}
                        maxLength={255}
                        onChange={(e) => setNewZoneName(e.target.value)}
                        placeholder="New zone name, e.g. Sponsor Attestations"
                        className="input w-full mt-2"
                      />
                    )}
                  </Field>
                  <Field label="Section" required>
                    <select
                      value={
                        sectionMode === 'other'
                          ? OTHER_SECTION_SENTINEL
                          : (form.parent_section_code ?? '')
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === OTHER_SECTION_SENTINEL) {
                          setSectionMode('other');
                          set('parent_section_code', null);
                          return;
                        }
                        setSectionMode('pick');
                        set('parent_section_code', v || null);
                      }}
                      disabled={(!selectedZoneName && zoneMode !== 'other') || taxonomyQ.isLoading}
                      className="input w-full"
                    >
                      <option value="">— Select section —</option>
                      {sectionsInSelectedZone.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.code} · {s.name}
                        </option>
                      ))}
                      {isPlatformAdmin && mode === 'create' && (
                        <option value={OTHER_SECTION_SENTINEL}>
                          + Other… (add a new section
                          {zoneMode === 'other' ? ' under the new zone' : ''})
                        </option>
                      )}
                    </select>
                    {sectionMode === 'other' && (
                      <div className="mt-2 space-y-2 rounded border border-accent-200 bg-accent-50/40 p-3">
                        <div className="text-[11px] font-medium uppercase tracking-wider text-accent-700">
                          New section
                        </div>
                        <input
                          type="text"
                          value={newSection.name}
                          maxLength={255}
                          onChange={(e) => setNewSection((s) => ({ ...s, name: e.target.value }))}
                          placeholder="Section name, e.g. Sponsor Attestations"
                          className="input w-full text-sm"
                        />
                        <select
                          value={newSection.inclusion}
                          onChange={(e) =>
                            setNewSection((s) => ({
                              ...s,
                              inclusion: e.target.value as 'core' | 'recommended',
                            }))
                          }
                          className="input w-full text-sm"
                        >
                          <option value="recommended">Recommended</option>
                          <option value="core">Core</option>
                        </select>
                      </div>
                    )}
                  </Field>
                </div>
                {sectionMode === 'pick' && selectedSection && (
                  <div className="mt-3 rounded border border-surface-border bg-surface-muted/40 p-3 text-xs">
                    <div className="font-medium text-ink-primary">
                      Artifact: {selectedSection.artifactType ?? '—'}
                    </div>
                    {selectedSection.subartifacts.length > 0 && (
                      <div className="mt-1 text-ink-muted">
                        Known sub-artifacts:{' '}
                        {selectedSection.subartifacts.slice(0, 6).join(', ')}
                        {selectedSection.subartifacts.length > 6
                          ? ` … (+${selectedSection.subartifacts.length - 6} more)`
                          : ''}
                      </div>
                    )}
                  </div>
                )}
                {mode === 'edit' && !form.parent_section_code && (
                  <div className="mt-3 rounded border border-warning-200 bg-warning-50 p-3 text-xs text-warning-800">
                    This row has no Section assigned. It appears in the eISF Catalog's
                    "Unmapped" bucket until you pick one here.
                  </div>
                )}
              </Section>

              <Section
                id="identity"
                title="Identity"
                intro={
                  mode === 'create'
                    ? 'The human-facing name. A canonical code is derived automatically and locked once the type is created.'
                    : 'The human-facing name. The code is immutable — every existing binder slot references it.'
                }
              >
                <Field label="Name" required error={touched ? validationErrors.document_type_name : undefined}>
                  <input
                    type="text"
                    value={form.document_type_name}
                    onChange={(e) => set('document_type_name', e.target.value)}
                    className="form-input"
                    placeholder="e.g. Informed Consent Form"
                  />
                </Field>
                <Field label="Definition" hint="Prose shown to coordinators on the type picker.">
                  <textarea
                    value={form.definition}
                    onChange={(e) => set('definition', e.target.value)}
                    rows={3}
                    className="form-input"
                  />
                </Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Toggle
                    label="Active"
                    checked={form.is_active}
                    onChange={(v) => set('is_active', v)}
                    hint="Off = hidden from the binder picker."
                  />
                  <Toggle
                    label="Visible to site users"
                    checked={!!form.visible_to_site_users}
                    onChange={(v) => set('visible_to_site_users', v)}
                    hint="Surfaces on the Site Documentation page."
                  />
                  <Toggle
                    label="Mandatory"
                    checked={!!form.is_mandatory}
                    onChange={(v) => set('is_mandatory', v)}
                    hint="Every binder template must include this. Site admins can rename it but not remove it."
                  />
                </div>
              </Section>

              <Section id="lifecycle" title="Lifecycle & approval" intro="How versions evolve and who signs off before a document goes live.">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Lock once approved?"
                    hint="Locked types can only be replaced via supersession. Replaceable types let newer versions overwrite."
                  >
                    <LifecycleLockToggle
                      stored={form.lifecycle}
                      onChange={(locked) => set('lifecycle', composeLifecycleFromLock(locked))}
                    />
                  </Field>
                  <Field
                    label="Create new draft versions"
                    hint="Whether a fresh draft can be spun up on top of this type."
                  >
                    <RadioWithConditional<DraftVersioningMode>
                      modes={DRAFT_VERSIONING_MODES}
                      value={form.create_new_draft_versions}
                      conditionalMode="Custom"
                      onChange={(next) => set('create_new_draft_versions', next)}
                      conditionalPlaceholder="e.g. 'No, should be distinct documents and only up-versioned in cases of correction or clarification.'"
                    />
                  </Field>
                </div>
                <Field
                  label="Approval mode"
                  hint="Single = one approver. Parallel = all sign concurrently. Sequential = signers act in order."
                >
                  <div className="grid grid-cols-3 gap-2">
                    {(['single', 'parallel', 'sequential'] as ApprovalMode[]).map((m) => (
                      <label
                        key={m}
                        className={`flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-sm ${
                          form.approval_mode === m
                            ? 'border-accent-600 bg-accent-50 font-medium text-accent-700'
                            : 'border-surface-border text-ink-secondary hover:bg-surface-muted/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="approval_mode"
                          value={m}
                          checked={form.approval_mode === m}
                          onChange={() => {
                            const patch: Partial<FormShape> = { approval_mode: m };
                            if (m !== 'parallel') patch.any_of_threshold = null;
                            if (m === 'single' && form.approvers.length > 1) {
                              patch.approvers = form.approvers.slice(0, 1);
                            }
                            setForm((f) => ({ ...f, ...patch }));
                            setTouched(true);
                          }}
                          className="h-4 w-4"
                        />
                        {m[0]!.toUpperCase() + m.slice(1)}
                      </label>
                    ))}
                  </div>
                </Field>

                {form.approval_mode === 'parallel' && (
                  <Field
                    label="Any-of threshold (optional)"
                    hint={`Leave blank for all-of. Set N to require N of ${form.approvers.length || 'K'} signatures.`}
                    error={touched ? validationErrors.any_of_threshold : undefined}
                  >
                    <input
                      type="number"
                      min={1}
                      max={form.approvers.length || undefined}
                      value={form.any_of_threshold ?? ''}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        set('any_of_threshold', v === '' ? null : Math.max(1, parseInt(v, 10) || 1));
                      }}
                      placeholder="All-of"
                      className="form-input max-w-[160px]"
                    />
                  </Field>
                )}

                <Field
                  label="Approvers"
                  hint={
                    form.approval_mode === 'single'
                      ? 'Exactly one approver row required.'
                      : form.approval_mode === 'sequential'
                      ? 'Order matters — lower sequence numbers sign first.'
                      : 'All approvers act concurrently. Sequence drives display order.'
                  }
                  error={touched ? validationErrors.approvers : undefined}
                >
                  <ApproverList
                    approvers={form.approvers}
                    mode={form.approval_mode}
                    onChange={(next) => set('approvers', next)}
                  />
                </Field>
              </Section>

              <Section
                id="workflows"
                title="Workflow actions"
                intro="Pick the actions users can take in the document's menu at each state."
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Draft-state actions"
                    hint="Available while the document is still in draft."
                    error={touched ? validationErrors.draft_state_workflows_json : undefined}
                  >
                    <MultiSelectPicker
                      options={DRAFT_WORKFLOW_OPTIONS}
                      value={parseJsonStringArray(form.draft_state_workflows_json)}
                      onChange={(next) => set('draft_state_workflows_json', composeJsonStringArray(next))}
                      columns={1}
                    />
                  </Field>
                  <Field
                    label="Steady-state actions"
                    hint="Available once the document is finalized."
                    error={touched ? validationErrors.steady_state_workflows_json : undefined}
                  >
                    <MultiSelectPicker
                      options={STEADY_WORKFLOW_OPTIONS}
                      value={parseJsonStringArray(form.steady_state_workflows_json)}
                      onChange={(next) => set('steady_state_workflows_json', composeJsonStringArray(next))}
                      columns={1}
                    />
                  </Field>
                </div>
              </Section>

              <Section
                id="permissions"
                title="Permissions & visibility"
                intro="Who can create, view, download, share, and be trained on this type. Booleans for the two site roles are derived from the multiselect."
              >
                <Field
                  label="Who can create"
                  hint="Role groups allowed to upload. The binder hides the upload button for everyone else."
                  error={touched ? validationErrors.who_can_create_json : undefined}
                >
                  <MultiSelectPicker
                    options={ROLES_FOR_CREATE}
                    value={parseJsonStringArray(form.who_can_create_json)}
                    onChange={(next) => set('who_can_create_json', composeJsonStringArray(next))}
                    columns={2}
                    emptyHint="No role selected — nobody will be able to upload this type."
                  />
                </Field>

                <Field
                  label="Can monitors view"
                  hint="Whether sponsor / CRA / external monitors can see documents of this type."
                >
                  <RadioWithConditional<MonitorViewMode>
                    modes={MONITOR_VIEW_MODES}
                    value={form.can_monitors_view}
                    conditionalMode="Conditional"
                    onChange={(next) => set('can_monitors_view', next)}
                    conditionalPlaceholder="e.g. 'Yes, if steady state and monitor is on the study.'"
                  />
                </Field>

                {form.can_monitors_view === 'Conditional' && (
                  <Field
                    label="Extra monitor-view notes (optional)"
                    hint="Only needed when the Conditional policy needs more nuance."
                  >
                    <textarea
                      value={form.monitor_view_conditions ?? ''}
                      onChange={(e) => set('monitor_view_conditions', e.target.value || null)}
                      rows={2}
                      className="form-input"
                    />
                  </Field>
                )}

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Toggle
                    label="Monitor can download"
                    checked={form.monitor_can_download}
                    onChange={(v) => set('monitor_can_download', v)}
                  />
                  <Toggle
                    label="Site viewer can download"
                    checked={form.site_viewer_can_download}
                    onChange={(v) => set('site_viewer_can_download', v)}
                  />
                  <Toggle
                    label="Shareable with external portal"
                    checked={form.shareable_via_myveeva}
                    onChange={(v) => set('shareable_via_myveeva', v)}
                  />
                </div>

                <Toggle
                  label="Training required"
                  checked={!!form.training_required}
                  onChange={(v) => set('training_required', v)}
                  hint="When on, uploads spawn a pending training certification for staff in the roles selected below."
                />
                {form.training_required && (
                  <Field
                    label="Required for which study roles"
                    hint="Leave empty to assign every delegate on the study (legacy behaviour)."
                  >
                    <MultiSelectPicker
                      options={TRAINING_ROLE_OPTIONS}
                      value={form.training_required_roles ?? []}
                      onChange={(next) =>
                        set('training_required_roles', next.length > 0 ? next : null)
                      }
                      columns={2}
                      emptyHint="No role selected — every delegate on the study will be assigned."
                    />
                  </Field>
                )}
              </Section>

              {/* Optional notes — collapsed by default; not the common path. */}
              <section id="notes" className="scroll-mt-20 rounded-lg border border-surface-border bg-white shadow-sm">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  className="flex w-full items-center gap-2 rounded-lg px-5 py-4 text-left hover:bg-surface-muted/30"
                  aria-expanded={advancedOpen}
                  aria-controls="notes-body"
                >
                  {advancedOpen ? <ChevronDown className="h-4 w-4 text-ink-muted" /> : <ChevronRight className="h-4 w-4 text-ink-muted" />}
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-ink-primary">Optional notes</div>
                    <div className="text-[12px] text-ink-muted">Required upload metadata + internal admin notes.</div>
                  </div>
                </button>
                {advancedOpen && (
                  <div id="notes-body" className="space-y-4 border-t border-surface-border px-5 py-4">
                    <Field
                      label="Required upload fields"
                      hint="Metadata that must be captured at upload."
                      error={touched ? validationErrors.required_fields_json : undefined}
                    >
                      <MultiSelectPicker
                        options={REQUIRED_FIELD_OPTIONS}
                        value={parseJsonStringArray(form.required_fields_json)}
                        onChange={(next) => set('required_fields_json', composeJsonStringArray(next))}
                        columns={2}
                      />
                    </Field>
                    <Field label="Source notes" hint="Internal admin notes — audit context, decisions.">
                      <textarea
                        value={form.source_notes ?? ''}
                        onChange={(e) => set('source_notes', e.target.value || null)}
                        rows={2}
                        className="form-input"
                      />
                    </Field>
                  </div>
                )}
              </section>
            </main>
          </div>
        )}
        <style>{`
          .form-input {
            display: block;
            width: 100%;
            border: 1px solid var(--surface-border, #d8dce0);
            border-radius: 6px;
            padding: 8px 10px;
            font-size: 13.5px;
            color: #2c3034;
            background: #fff;
            outline: none;
          }
          .form-input:focus { border-color: #2f6fd6; box-shadow: 0 0 0 2px rgba(47,111,214,0.15); }
          .form-input[readonly] { color: #6b7280; }
        `}</style>
      </>
    </PageHeader>
  );
}

function Section({
  id,
  title,
  intro,
  children,
}: {
  id?: string;
  title: string;
  intro?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className="scroll-mt-20 rounded-lg border border-surface-border bg-white p-5 shadow-sm"
    >
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-ink-primary">{title}</h2>
        {intro && <p className="mt-0.5 text-[12px] text-ink-muted">{intro}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  required,
  error,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-secondary">
        {label}
        {required && <span className="text-danger-600 ml-0.5">*</span>}
      </span>
      <div className="mt-1">{children}</div>
      {hint && !error && <div className="mt-1 text-[11px] text-ink-muted">{hint}</div>}
      {error && <div className="mt-1 text-[11px] text-danger-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{error}</div>}
    </label>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="inline-flex items-start gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 mt-0.5"
      />
      <div className="min-w-0">
        <div className="text-xs font-medium text-ink-secondary">{label}</div>
        {hint && <div className="text-[11px] text-ink-muted">{hint}</div>}
      </div>
    </label>
  );
}

/**
 * Lock-once-approved toggle. Two radio cards (Locked / Replaceable) with
 * a legacy-value chip when the stored value isn't one of the two canonical
 * strings (e.g. "Draft to Approved for Use" from an older seed). Picking
 * a side overwrites the legacy value on save — the admin sees the legacy
 * tag and decides intentionally.
 */
function LifecycleLockToggle({
  stored,
  onChange,
}: {
  stored: string;
  onChange: (locked: boolean) => void;
}) {
  const { locked, legacyEndState } = parseLifecycleLock(stored);
  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        <label
          className={`flex items-start gap-2 cursor-pointer rounded border p-2.5 text-sm ${
            locked
              ? 'border-accent-600 bg-accent-50'
              : 'border-surface-border hover:bg-surface-muted/30'
          }`}
        >
          <input
            type="radio"
            checked={locked}
            onChange={() => onChange(true)}
            className="mt-0.5 h-4 w-4"
          />
          <span>
            <div className={`font-medium ${locked ? 'text-accent-700' : 'text-ink-primary'}`}>
              Locked
            </div>
            <div className="mt-0.5 text-[11px] text-ink-muted">
              Final after approval. Replaced only via supersession (audit trail).
              Use for Protocol, ICF, FDA submission, contracts.
            </div>
          </span>
        </label>
        <label
          className={`flex items-start gap-2 cursor-pointer rounded border p-2.5 text-sm ${
            !locked
              ? 'border-accent-600 bg-accent-50'
              : 'border-surface-border hover:bg-surface-muted/30'
          }`}
        >
          <input
            type="radio"
            checked={!locked}
            onChange={() => onChange(false)}
            className="mt-0.5 h-4 w-4"
          />
          <span>
            <div className={`font-medium ${!locked ? 'text-accent-700' : 'text-ink-primary'}`}>
              Replaceable
            </div>
            <div className="mt-0.5 text-[11px] text-ink-muted">
              Newer versions take over the slot automatically. Use for CV, License,
              Lab Cert, Investigator Brochure.
            </div>
          </span>
        </label>
      </div>
      {legacyEndState && (
        <div className="mt-2 inline-flex items-center gap-1.5 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
          <span>
            Legacy stored value: <span className="font-mono">Draft to {legacyEndState}</span> — will be saved as
            <span className="font-mono">{locked ? ' Draft to Final' : ' Draft to Current'}</span>.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Multi-select picker. Renders all canonical options as checkboxes (small
 * sets) or as a checkbox grid (medium sets). Legacy values present in the
 * data but not in the canonical list are surfaced with a "(legacy)" tag
 * so the admin can keep or remove them — never silently dropped.
 *
 * Storage stays JSON-array-as-string; the form deserializes on load and
 * serializes on submit via `parseJsonStringArray` / `composeJsonStringArray`.
 */
type PickerOption = string | { value: string; label: string };

function normaliseOption(opt: PickerOption): { value: string; label: string } {
  return typeof opt === 'string' ? { value: opt, label: opt } : opt;
}

function MultiSelectPicker({
  options,
  value,
  onChange,
  columns = 2,
  emptyHint,
}: {
  options: readonly PickerOption[];
  value: string[];
  onChange: (next: string[]) => void;
  columns?: 1 | 2 | 3;
  emptyHint?: string;
}) {
  const normalised = options.map(normaliseOption);
  const optionValues = new Set(normalised.map((o) => o.value));

  function toggle(v: string) {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  }
  const legacyValues = value.filter((v) => !optionValues.has(v));

  const gridCls =
    columns === 1
      ? 'grid grid-cols-1 gap-1.5'
      : columns === 2
      ? 'grid grid-cols-2 gap-1.5'
      : 'grid grid-cols-3 gap-1.5';

  return (
    <div className="rounded border border-surface-border bg-surface-muted/20 p-2">
      <div className={gridCls}>
        {normalised.map((opt) => {
          const checked = value.includes(opt.value);
          return (
            <label
              key={opt.value}
              className={`flex items-start gap-2 cursor-pointer rounded px-2 py-1 text-xs ${
                checked ? 'bg-accent-50' : 'hover:bg-surface-muted/40'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt.value)}
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
              />
              <span className={checked ? 'font-medium text-ink-primary' : 'text-ink-secondary'}>
                {opt.label}
              </span>
            </label>
          );
        })}
      </div>
      {legacyValues.length > 0 && (
        <div className="mt-2 border-t border-surface-border pt-2">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">
            Legacy values (kept until you remove them)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {legacyValues.map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800"
              >
                {v}
                <button
                  type="button"
                  onClick={() => onChange(value.filter((x) => x !== v))}
                  className="text-amber-700 hover:text-amber-900"
                  aria-label={`Remove ${v}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
      {value.length === 0 && emptyHint && (
        <div className="mt-2 text-[11px] italic text-ink-muted">{emptyHint}</div>
      )}
    </div>
  );
}

/**
 * Radio group with an optional conditional textarea revealed when one of
 * the values (e.g. "Conditional" / "Custom") is picked. Used for
 * can_monitors_view and create_new_draft_versions where the storage is
 * a single string column — either an enum value verbatim, or a long-form
 * string when the conditional/custom option is selected.
 */
function RadioWithConditional<T extends string>({
  modes,
  value,
  conditionalMode,
  onChange,
  conditionalPlaceholder,
}: {
  modes: readonly T[];
  /** The currently-stored raw string. May exactly match a mode, or be free text (= conditional mode). */
  value: string;
  /** Which mode triggers the textarea (e.g. 'Conditional' or 'Custom'). */
  conditionalMode: T;
  /** Receives the new raw string to store. */
  onChange: (next: string) => void;
  conditionalPlaceholder?: string;
}) {
  // Resolve the active mode: exact-match against the enum, or fall back
  // to the conditional mode when the stored string is non-empty free text.
  const exactMatch = (modes as readonly string[]).includes(value) ? (value as T) : null;
  const isConditional = !exactMatch && value.length > 0;
  const activeMode: T = exactMatch ?? (isConditional ? conditionalMode : modes[0]!);

  function pick(mode: T) {
    if (mode === conditionalMode) {
      // Keep existing free text if we already have it; otherwise initialize empty
      // so the textarea opens with focus on empty input.
      if (!exactMatch) return;
      onChange('');
    } else {
      onChange(mode);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {modes.map((m) => {
          const checked = activeMode === m;
          return (
            <label
              key={m}
              className={`flex items-center gap-2 cursor-pointer rounded border px-3 py-1.5 text-xs ${
                checked
                  ? 'border-accent-600 bg-accent-50 text-accent-700 font-medium'
                  : 'border-surface-border text-ink-secondary hover:bg-surface-muted/30'
              }`}
            >
              <input
                type="radio"
                checked={checked}
                onChange={() => pick(m)}
                className="h-3.5 w-3.5"
              />
              {m}
            </label>
          );
        })}
      </div>
      {activeMode === conditionalMode && (
        <textarea
          value={exactMatch ? '' : value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          placeholder={conditionalPlaceholder}
          className="form-input mt-2"
        />
      )}
    </div>
  );
}

function ApproverList({
  approvers,
  mode,
  onChange,
}: {
  approvers: DocumentTypeApproverInput[];
  mode: ApprovalMode;
  onChange: (next: DocumentTypeApproverInput[]) => void;
}) {
  const canAdd = mode !== 'single' || approvers.length === 0;
  const nextSequenceOrder = () => {
    if (approvers.length === 0) return 1;
    if (mode === 'sequential') {
      // Default new row to one step after the current highest, so the
      // admin sees the chain extend naturally.
      return Math.max(...approvers.map((a) => a.sequence_order)) + 1;
    }
    // Parallel: same step as the rest by default.
    return Math.min(...approvers.map((a) => a.sequence_order));
  };

  function addRow() {
    onChange([
      ...approvers,
      {
        role: 'principal_investigator',
        sequence_order: nextSequenceOrder(),
        signature_meaning: DEFAULT_SIGNATURE_MEANINGS.principal_investigator ?? '',
      },
    ]);
  }

  function updateRow(i: number, patch: Partial<DocumentTypeApproverInput>) {
    onChange(approvers.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }

  function removeRow(i: number) {
    onChange(approvers.filter((_, idx) => idx !== i));
  }

  return (
    <div className="space-y-2">
      {approvers.length === 0 && (
        <div className="rounded border border-dashed border-surface-border bg-surface-muted/30 p-3 text-xs text-ink-muted">
          No approvers yet. {mode === 'single' ? 'Add the one approver below.' : 'Add the required approvers below.'}
        </div>
      )}

      {approvers.map((a, i) => {
        const isDup = approvers.some(
          (other, j) => j !== i && other.role === a.role && other.sequence_order === a.sequence_order,
        );
        return (
          <div
            key={i}
            className={`grid grid-cols-12 gap-2 items-start rounded border p-2 ${
              isDup ? 'border-danger-300 bg-danger-50' : 'border-surface-border bg-surface-muted/20'
            }`}
          >
            <div className="col-span-1 flex items-center justify-center pt-1.5 text-ink-muted">
              <GripVertical className="h-3.5 w-3.5" />
            </div>
            <div className="col-span-4">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-0.5">Role</div>
              <select
                value={a.role}
                onChange={(e) => {
                  const role = e.target.value;
                  const patch: Partial<DocumentTypeApproverInput> = { role };
                  // If the meaning is still the default for the old role,
                  // swap to the default for the new role. Otherwise leave
                  // the admin's custom value alone.
                  const oldDefault = DEFAULT_SIGNATURE_MEANINGS[a.role];
                  if (a.signature_meaning === oldDefault || a.signature_meaning === '') {
                    patch.signature_meaning = DEFAULT_SIGNATURE_MEANINGS[role] ?? '';
                  }
                  updateRow(i, patch);
                }}
                className="form-input"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {humanRole(r)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-0.5">Sequence</div>
              <input
                type="number"
                min={1}
                value={a.sequence_order}
                onChange={(e) => updateRow(i, { sequence_order: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                disabled={mode === 'single'}
                className={`form-input ${mode === 'single' ? 'bg-surface-muted/50' : ''}`}
              />
            </div>
            <div className="col-span-4">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-0.5">Signature meaning (§11.50)</div>
              <input
                type="text"
                value={a.signature_meaning}
                onChange={(e) => updateRow(i, { signature_meaning: e.target.value })}
                placeholder="e.g. I approve this protocol for use at my site."
                className="form-input"
              />
            </div>
            <div className="col-span-1 flex items-start justify-end pt-5">
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="text-ink-muted hover:text-danger-600 p-1"
                aria-label="Remove approver"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            {isDup && (
              <div className="col-span-12 text-[11px] text-danger-700 flex items-center gap-1 mt-1">
                <AlertTriangle className="h-3 w-3" />
                Duplicate: another row has the same role + sequence.
              </div>
            )}
          </div>
        );
      })}

      {canAdd && (
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1.5 rounded border border-dashed border-accent-300 px-3 py-1.5 text-xs font-medium text-accent-700 hover:bg-accent-50"
        >
          <Plus className="h-3 w-3" />
          Add approver
        </button>
      )}
    </div>
  );
}

function isJsonArrayString(s: string): boolean {
  try {
    return Array.isArray(JSON.parse(s));
  } catch {
    return false;
  }
}

function validate(form: FormShape, mode: Mode): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!form.document_type_name.trim()) {
    errs.document_type_name = 'Required';
  }
  if (mode === 'create') {
    const derived = form.document_type_code.trim();
    if (form.document_type_name.trim() && (!derived || !SLUG_PATTERN.test(derived))) {
      errs.document_type_name = 'Name must contain at least two letters or numbers.';
    }
    // Filing location is REQUIRED on create so new rows can never land
    // orphaned. Three valid paths:
    //   1. parent_section_code set (existing section picked)
    //   2. sectionMode = 'other' with all three new_section fields filled
    //      AND either zoneMode = 'other' with newZoneName set OR a
    //      pre-existing selectedZoneName
    // Anything else fails.
    // (validate runs outside the component's scope but reads the same
    //  form + local pickers via closure via the useMemo below.)
  }
  if (!isJsonArrayString(form.who_can_create_json)) {
    errs.who_can_create_json = 'Must be a JSON array, e.g. ["Role A","Role B"]';
  }
  if (!isJsonArrayString(form.required_fields_json)) {
    errs.required_fields_json = 'Must be a JSON array';
  }
  if (!isJsonArrayString(form.draft_state_workflows_json)) {
    errs.draft_state_workflows_json = 'Must be a JSON array';
  }
  if (!isJsonArrayString(form.steady_state_workflows_json)) {
    errs.steady_state_workflows_json = 'Must be a JSON array';
  }

  // Approval routing shape — mirror the BE validateApproverShape so the
  // submit button only enables when the BE would accept.
  if (form.approval_mode === 'single' && form.approvers.length !== 1) {
    errs.approvers = `Single-approver mode needs exactly one approver row (got ${form.approvers.length}).`;
  }
  if (form.approval_mode !== 'single' && form.approvers.length === 0) {
    errs.approvers = 'Add at least one approver row.';
  }
  if (form.approval_mode === 'parallel' && form.any_of_threshold !== null) {
    if (form.any_of_threshold < 1 || form.any_of_threshold > form.approvers.length) {
      errs.any_of_threshold = `Must be between 1 and ${form.approvers.length} (the number of approvers).`;
    }
  }
  // Duplicate (role, sequence_order) detection — same as the BE.
  const seen = new Set<string>();
  for (const a of form.approvers) {
    const k = `${a.role}::${a.sequence_order}`;
    if (seen.has(k)) {
      errs.approvers = errs.approvers ?? 'Duplicate approver rows (same role + sequence number).';
    }
    seen.add(k);
  }
  // Each approver must have a signature meaning.
  if (form.approvers.some((a) => !a.signature_meaning.trim())) {
    errs.approvers = errs.approvers ?? 'Every approver needs a §11.50 signature meaning.';
  }
  return errs;
}
